import {
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { type Locale, format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useAtomValue } from 'jotai';

import type { StatusChipProps } from '../../../../common-components/elements/StatusChip/StatusChip';
import { useGetDateFNSLocale } from '../../../../locales/helpers';
import { selectedPlanAtom } from '../../atoms';
import { isPlanActive } from '../../helpers';

import { BenefitsProgress } from './BenefitsProgress';

export const formatLastRunDate = (
  dateString: string | null,
  locale: Locale = enUS,
): string => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return format(date, 'P', { locale });
  } catch {
    return 'Invalid date';
  }
};

export interface PlanSummaryColumnsProps {
  benefitsTitle: string;
  remainingBenefits: number;
  totalBenefits: number;
  benefitsChip?: StatusChipProps;
  benefitsNoDataMessage?: string;
  deductibleTitle: string;
  remainingDeductible: number;
  totalDeductible: number;
  noDeductibleMessage?: string;
  isDeductibleMet: boolean;
  hideBenefitsProgress?: boolean;
}

export const PlanSummaryColumns = ({
  benefitsTitle,
  remainingBenefits,
  totalBenefits,
  benefitsChip,
  benefitsNoDataMessage,
  deductibleTitle,
  remainingDeductible,
  totalDeductible,
  noDeductibleMessage,
  isDeductibleMet,
  hideBenefitsProgress,
}: PlanSummaryColumnsProps) => {
  const dateLocale = useGetDateFNSLocale();
  const currentPlan = useAtomValue(selectedPlanAtom);
  const isActive = isPlanActive(currentPlan?.plan?.status ?? null);
  const lastRun = currentPlan?.lastUpdated ?? null;

  return (
    <div className="vai-space-y-6">
      <div className="vai-flex vai-gap-8">
        {/* Plan Status */}
        <div>
          <div className="vai-text-sm vai-font-medium vai-text-muted-foreground vai-uppercase vai-leading-small">
            <Trans>PLAN STATUS</Trans>
          </div>
          <div className="vai-mt-3 vai-flex vai-items-center vai-gap-2">
            <FontAwesomeIcon
              icon={isActive ? faCheckCircle : faTimesCircle}
              className={`vai-h-4 vai-w-4 ${
                isActive ? 'vai-text-green-700' : 'vai-text-red-500'
              }`}
            />
            <span
              className={`${
                isActive
                  ? 'vai-text-green-700 vai-text-2xl vai-font-medium'
                  : 'vai-text-red-600 vai-text-2xl vai-font-medium'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          {lastRun && (
            <div className="vai-mt-2 vai-text-sm vai-text-gray-500">
              <Trans>Last run:</Trans> {formatLastRunDate(lastRun, dateLocale)}
            </div>
          )}
        </div>

        <div className="vai-w-px vai-bg-gray-200" />

        {/* Benefits Remaining */}
        <div className="vai-flex-1">
          <BenefitsProgress
            progress={remainingBenefits}
            total={totalBenefits}
            title={benefitsTitle}
            hideProgress={hideBenefitsProgress}
            chip={benefitsChip}
            noDataMessage={benefitsNoDataMessage}
          />
        </div>

        <div className="vai-w-px vai-bg-gray-200" />

        {/* Deductible Remaining */}
        <div className="vai-flex-1">
          <BenefitsProgress
            progress={remainingDeductible}
            total={totalDeductible}
            title={deductibleTitle}
            progressColor="vai-bg-primary"
            hideProgress={isDeductibleMet}
            noDataMessage={noDeductibleMessage}
            chip={
              isDeductibleMet
                ? { text: <Trans>Deductible Met</Trans>, variant: 'success' }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};
